import { Document, DocumentSearchParams, DocumentsResponse } from "@/models/Document";
import { DocumentType } from "@/models/DocumentType";
import { DocumentGroup } from "@/models/DocumentGroup";
import { DocumentForm, FormStep, FormField } from "@/models/Form";
import { apiRequest, SUPABASE_ANON_KEY } from "./apiUtils";
import { config } from "@/config";
import { createApiWrapper } from "./apiWrapper";

// Use the correct Supabase REST API URL
const SUPABASE_REST_URL = `${config.SUPABASE_URL}/rest/v1`;

// First, create a simple API wrapper
export const documentApi = createApiWrapper<Document>('documents');
export const documentTypeApi = createApiWrapper<DocumentType>('document-types');
const baseDocumentGroupApi = createApiWrapper<DocumentGroup>('document-groups');
export const documentFormApi = createApiWrapper<DocumentForm>('document-forms');
export const formStepApi = createApiWrapper<FormStep>('form-steps');
export const formFieldApi = createApiWrapper<FormField>('form-fields');

// Enhance the documentGroupApi with custom filtering
export const documentGroupApi = {
  ...baseDocumentGroupApi,
  listWithFilters: async (filters: { management_company_id?: number }): Promise<DocumentGroup[]> => {
    const queryParams = new URLSearchParams();
    
    if (filters.management_company_id) {
      queryParams.append('management_company_id', filters.management_company_id.toString());
    }
    
    // Make the API request to the edge function
    return apiRequest<DocumentGroup[]>(
      `${config.SUPABASE_URL}/functions/v1/document-groups?${queryParams.toString()}`
    );
  }
};

// Then enhance it with custom list method that supports search params
export const enhancedDocumentApi = {
  ...documentApi,
  list: async (params: DocumentSearchParams): Promise<DocumentsResponse> => {
    // Build query string from params
    const queryParams = new URLSearchParams();
    
    // Remove role-based filtering parameters - backend now handles this automatically
    
    if (params.search !== undefined) {
      queryParams.append('search', params.search);
    }
    
    if (params.document_type_id !== undefined) {
      queryParams.append('document_type_id', params.document_type_id.toString());
    }
    
    if (params.publicly_available !== undefined) {
      queryParams.append('publicly_available', params.publicly_available.toString());
    }
    
    if (params.status !== undefined) {
      queryParams.append('status', params.status);
    }
    
    // Handle array of document_group_ids
    if (params.document_group_ids && params.document_group_ids.length > 0) {
      params.document_group_ids.forEach(id => {
        queryParams.append('document_group_ids', id.toString());
      });
    }
    
    if (params.limit !== undefined) {
      queryParams.append('limit', params.limit.toString());
    }
    
    if (params.offset !== undefined) {
      queryParams.append('offset', params.offset.toString());
    }

    // Make the API request to the edge function
    return apiRequest<DocumentsResponse>(
      `${config.SUPABASE_URL}/functions/v1/documents?${queryParams.toString()}`
    );
  },

  /**
   * Duplicate a document with its form structure
   */
  duplicate: async (documentId: number, newName: string): Promise<Document> => {
    return apiRequest<Document>(
      `${config.SUPABASE_URL}/functions/v1/documents/${documentId}/duplicate`,
      {
        method: 'POST',
        body: { newName }
      }
    );
  },

  /**
   * Create a document attachment
   */
  async createAttachment(documentId: number, attachmentData: {
    file_path: string;
    file_name?: string;
    file_size?: number;
    mime_type?: string;
    status?: 'upcoming' | 'current' | 'expired' | 'disabled';
    expires_at?: string;
  }) {
    const result = await apiRequest<any>(`${SUPABASE_REST_URL}/document_attachments?select=*`, {
      method: "POST",
      headers: {
        "Prefer": "return=representation"
      },
      body: {
        document_id: documentId,
        status: 'current', // Default to current
        ...attachmentData,
      },
    });

    // Fire-and-forget: train the AI knowledge base from the new file when the
    // management company has it enabled (the function checks the flag itself).
    const attachmentId = Array.isArray(result) ? result[0]?.id : (result as any)?.id;
    if (attachmentId) {
      apiRequest(`${config.SUPABASE_URL}/functions/v1/ai-process-document`, {
        method: "POST",
        body: { document_attachment_id: attachmentId },
      }).catch((err) => console.log("[AI] Document processing trigger skipped:", (err as Error)?.message));
    }

    return result;
  },

  /**
   * List attachments for a document
   */
  async listAttachments(documentId: number) {
    return apiRequest(
      `${SUPABASE_REST_URL}/document_attachments?document_id=eq.${documentId}&order=created_at.desc`
    );
  },

  /**
   * Update attachment status
   */
  async updateAttachmentStatus(attachmentId: number, status: 'upcoming' | 'current' | 'expired' | 'disabled', expires_at?: string) {
    return apiRequest(`${SUPABASE_REST_URL}/document_attachments?id=eq.${attachmentId}&select=*`, {
      method: "PATCH",
      headers: {
        "Prefer": "return=representation"
      },
      body: {
        status,
        ...(expires_at && { expires_at }),
      },
    });
  },

  /**
   * Set attachment expiration
   */
  async setAttachmentExpiration(attachmentId: number, expires_at: string) {
    return apiRequest(`${SUPABASE_REST_URL}/document_attachments?id=eq.${attachmentId}&select=*`, {
      method: "PATCH",
      headers: {
        "Prefer": "return=representation"
      },
      body: {
        expires_at,
      },
    });
  },

  /**
   * Set attachment effective date
   */
  async setAttachmentEffectiveDate(attachmentId: number, effective_date: string) {
    return apiRequest(`${SUPABASE_REST_URL}/document_attachments?id=eq.${attachmentId}&select=*`, {
      method: "PATCH",
      headers: {
        "Prefer": "return=representation"
      },
      body: {
        effective_date,
      },
    });
  },

  /**
   * Delete a document attachment
   */
  async deleteAttachment(attachmentId: number) {
    return apiRequest(`${SUPABASE_REST_URL}/document_attachments?id=eq.${attachmentId}`, {
      method: "DELETE",
    });
  },

  /**
   * Get expired attachments
   */
  async getExpiredAttachments() {
    return apiRequest(
      `${SUPABASE_REST_URL}/document_attachments?status=eq.expired&order=expires_at.desc`
    );
  },

  /**
   * Manually trigger expiration check
   */
  async expireAttachments() {
    return apiRequest(`${SUPABASE_REST_URL}/rpc/expire_document_attachments`, {
      method: "POST",
    });
  },
};
